import { pick, uniq, isPlainObject } from 'lodash'
import { captureSentryException } from '@edulastic/common'
import { questionType } from '@edulastic/constants'
import { alignmentStandardsFromUIToMongo } from '../../../assessment/utils/helpers'
import gradesMap from '../../Reports/common/static/json/gradesMap.json'
import { isMathTextFieldEmpty } from '../../questionUtils'

const {
  MULTIPLE_CHOICE,
  ESSAY_RICH_TEXT,
  ESSAY_PLAIN_TEXT,
  CLOZE_DRAG_DROP,
  CLOZE_DROP_DOWN,
  CLOZE_TEXT,
  CLOZE_IMAGE_DRAG_DROP,
  CLOZE_IMAGE_DROP_DOWN,
  CLOZE_IMAGE_TEXT,
  EXPRESSION_MULTIPART,
  EDITING_TASK,
  CHOICE_MATRIX,
} = questionType

/**
 * Question types that only require stimulus (no options) to generate metadata
 */
const STIMULUS_ONLY_QTYPES = [
  ESSAY_RICH_TEXT,
  ESSAY_PLAIN_TEXT,
  CLOZE_TEXT,
  CLOZE_IMAGE_TEXT,
]

/**
 * Builds tooltip items for a standard using curriculum details
 * @param {Object} standard - Standard object with curriculumId and grades
 * @param {Array} allCurriculums - List of all curriculums
 * @returns {Array<{ label: string, value: string }>} - Tooltip items
 */
export const getStandardTooltipItems = (standards, curriculum) => {
  const curriculumName = curriculum?.curriculum || ''
  const subject = curriculum?.subject || ''
  const grades = uniq(
    (standards.flatMap((s) => s.grades || []) || []).filter(Boolean)
  )
    .slice()
    .sort(
      (a, b) =>
        (gradesMap[a]?.sortKey ?? Infinity) -
        (gradesMap[b]?.sortKey ?? Infinity)
    )

  return [
    curriculumName && { label: 'Standard Set', value: curriculumName },
    subject && { label: 'Subject', value: subject },
    grades.length > 0 && { label: 'Grade', value: grades.join(', ') },
  ].filter(Boolean)
}

export const getStandardCurriculumData = (standard, allCurriculums = []) => {
  if (!standard?.curriculumId || !allCurriculums.length) {
    return {}
  }

  const curriculum = allCurriculums.find((c) => c._id === standard.curriculumId)
  return curriculum
}

const standardFields = [
  '_id',
  'level',
  'grades',
  'identifier',
  'tloIdentifier',
  'tloId',
  'tloDescription',
  'eloId',
  'subEloId',
  'description',
  'curriculumId',
  'pdlId',
]

/**
 * Helper to extract options from object format { responseId: ['Option A', 'Option B'] }
 * @param {Object} optionsObj - Options object
 * @returns {string[]} - Flattened array of option strings
 */
const extractOptionsFromObject = (optionsObj) => {
  if (!isPlainObject(optionsObj)) {
    return []
  }

  return Object.values(optionsObj)
    .flat()
    .filter((opt) => typeof opt === 'string' && opt)
}

const extractOptionsForExpressionMultipart = (question) => {
  const options = extractOptionsFromObject(question.options)
  const validResponse = question?.validation?.validResponse

  if (validResponse) {
    const { dropdown, mathUnits, textinput, value } = validResponse

    if (dropdown && dropdown.value) {
      for (const opt of dropdown.value) {
        if (!opt.value || (opt.value && !opt.value.trim())) {
          return []
        }
      }
    }

    if (mathUnits && mathUnits.value) {
      for (const opt of mathUnits.value) {
        if (isMathTextFieldEmpty(opt.value) || !opt.options.unit) {
          return []
        }
        options.push(opt.value)
      }
    }

    if (textinput && textinput.value) {
      for (const opt of textinput.value) {
        if (!opt.value || (opt.value && !opt.value.trim())) {
          return []
        }
        options.push(opt.value)
      }
    }

    if (value && value.length) {
      for (const opt of value) {
        for (const _opt of opt) {
          if (isMathTextFieldEmpty(_opt.value)) {
            return []
          }
          options.push(_opt.value)
        }
      }
    }
  }

  return options
}

/**
 * Builds the payload for AI metadata generation API based on question type
 * @param {Object} question - Question data object
 * @returns {Object} - { stimulus: string, options: string[] }
 */
export const buildQuestionMetadataPayload = (question) => {
  if (!question) {
    return { stimulus: '', options: [] }
  }

  const { type, stimulus = '' } = question
  let options = []

  switch (type) {
    case MULTIPLE_CHOICE:
      options = (question.options || [])
        .map((opt) => opt?.label)
        .filter(Boolean)
      break

    case CHOICE_MATRIX:
      options = (question.stems || []).filter(Boolean)
      break

    case CLOZE_DRAG_DROP:
      options = (question.options || [])
        .map((opt) => opt?.label)
        .filter(Boolean)
      break

    case CLOZE_DROP_DOWN:
      options = extractOptionsFromObject(question.options)
      break

    case CLOZE_IMAGE_DRAG_DROP:
      options = (question.options || [])
        .map((opt) => opt?.value)
        .filter(Boolean)
      break

    case CLOZE_IMAGE_DROP_DOWN:
      options = (question.options || []).flat().filter(Boolean)
      break

    case EXPRESSION_MULTIPART:
      options = extractOptionsForExpressionMultipart(question)
      break

    case EDITING_TASK:
      options = extractOptionsFromObject(question.options)
      break

    case ESSAY_RICH_TEXT:
    case ESSAY_PLAIN_TEXT:
    case CLOZE_TEXT:
    case CLOZE_IMAGE_TEXT:
      break

    default:
      break
  }

  return {
    stimulus,
    qType: type,
    ...(options?.length > 0 ? { options } : {}),
  }
}

/**
 * Checks if a question has sufficient content for AI metadata generation
 * based on the question type
 * @param {Object} question - Question data object
 * @returns {boolean} - Whether the question has enough content
 */
export const hasQuestionContentForMetadata = (question) => {
  if (!question) return false

  const { type, stimulus } = question
  const hasStimulusContent =
    typeof stimulus === 'string' && stimulus.trim().length > 0

  if (STIMULUS_ONLY_QTYPES.includes(type)) {
    return hasStimulusContent
  }

  const formattedQuestion = buildQuestionMetadataPayload(question)
  const { options } = formattedQuestion

  const hasOptionsContent = Array.isArray(options) && options.length > 0

  return hasStimulusContent && hasOptionsContent
}

/**
 * @param {Object} params
 * @param {Object} params.suggestedMetaData - Original AI-suggested metadata
 * @param {Object} params.userUpdatedData - User edits that override AI suggestions
 * @returns {{ dok: string|null, difficulty: string|null, standards: Array }}
 */
export const getEffectiveMetadata = ({
  suggestedMetaData = {},
  userUpdatedData = {},
}) => {
  const dok = userUpdatedData.dok || suggestedMetaData?.dok || null
  const difficulty =
    userUpdatedData.difficulty || suggestedMetaData?.difficulty || null
  const standards = userUpdatedData.standards || []

  return { dok, difficulty, standards }
}

/**
 * @param {Array} standards - Selected standards
 * @returns {string|null}
 */
export const getStandardIdsToLog = (standards = []) =>
  (standards || [])
    .map((s) => s._id)
    .filter(Boolean)
    .join(',') || null

/**
 * @param {Object} suggestedMetaData - Original AI-suggested metadata from state
 * @returns {{ dok: string|null, difficulty: string|null, standardIds: string[] }}
 */
export const getFormattedAISuggestedData = (suggestedMetaData = {}) => {
  const { dok = null, difficulty = null, standards = [] } =
    suggestedMetaData || {}
  const standardIds = standards?.map((s) => s._id).filter(Boolean) || []

  return {
    ...(dok && { dok }),
    ...(difficulty && { difficulty }),
    ...(standardIds?.length > 0 && { standardIds }),
  }
}

/**
 * @param {Object} params - Parameters object
 * @param {Object} params.question - Current question data
 * @param {Object} params.metadata - Resolved metadata { dok, difficulty, standards }
 * @param {Array} params.allCurriculums - List of all curriculums from state
 * @param {Array} params.dictAlignments - Dictionaries alignments with standards in UI format
 * @returns {Object} - Updated question with metadata applied
 */
export const applyAIMetadataToQuestion = ({
  question,
  metadata = {},
  allCurriculums,
  dictAlignments,
}) => {
  const { dok, difficulty, standards = [] } = metadata

  if (!standards || standards.length === 0) {
    return {
      ...question,
      ...(dok && { depthOfKnowledge: dok }),
      ...(difficulty && { authorDifficulty: difficulty }),
    }
  }

  const newStandards = standards.map((s) => pick(s, standardFields))
  const curriculumId = newStandards[0]?.curriculumId
  const curriculum = allCurriculums.find((c) => c._id === curriculumId)

  if (!curriculum) {
    captureSentryException(
      new Error('Curriculum not found for suggested standard'),
      {
        errorMessage: 'AI Question Assistance - METADATA: Curriculum not found',
        standards: newStandards,
        curriculumId,
      }
    )
    return question
  }

  const existingAlignments = (question.alignment || []).filter(
    (a) => !a.isEquivalentStandard
  )

  const existingAlignmentIndex = existingAlignments.findIndex(
    (a) => a.curriculumId === curriculumId
  )

  let updatedAlignments
  let questionGrades = []

  if (existingAlignmentIndex !== -1) {
    const dictAlignment = dictAlignments.find(
      (a) => a.curriculumId === curriculumId
    )
    const existingStandards = dictAlignment?.standards || []

    const mergedStandards = [...existingStandards]
    for (const newStd of newStandards) {
      if (!existingStandards.some((s) => s._id === newStd._id)) {
        mergedStandards.push(newStd)
      }
    }

    questionGrades = uniq(
      mergedStandards.flatMap((s) => s.grades || [])
    ).filter(Boolean)

    const updatedDomains = alignmentStandardsFromUIToMongo(mergedStandards)

    updatedAlignments = existingAlignments.map((alignment, index) =>
      index === existingAlignmentIndex
        ? { ...alignment, grades: [], domains: updatedDomains }
        : alignment
    )
  } else {
    questionGrades = uniq(newStandards.flatMap((s) => s.grades || [])).filter(
      Boolean
    )

    const newQuestionAlignment = {
      curriculum: curriculum.curriculum,
      curriculumId,
      subject: curriculum.subject,
      grades: [],
      domains: alignmentStandardsFromUIToMongo(newStandards),
    }
    updatedAlignments = [...existingAlignments, newQuestionAlignment]
  }

  return {
    ...question,
    alignment: updatedAlignments,
    grades: questionGrades,
    subjects: [curriculum.subject].filter(Boolean),
    ...(dok && { depthOfKnowledge: dok }),
    ...(difficulty && { authorDifficulty: difficulty }),
  }
}

/**
 * @param {Array} interestedCurriculums - from getAllInterestedCurriculumsSelector
 * @param {Array} interestedSubjects - from getInterestedSubjectsSelector
 * @returns {{ standardSetOptions: Array }} Options for the standard set dropdown; empty when user has no interested standard sets
 */
export const getStandardSetOptions = ({
  interestedCurriculums,
  interestedSubjects,
}) => {
  try {
    const userInterestedCurriculums = Array.isArray(interestedCurriculums)
      ? interestedCurriculums
      : []
    const userInterestedSubjects = Array.isArray(interestedSubjects)
      ? interestedSubjects
      : []

    if (userInterestedCurriculums.length === 0) {
      return {
        standardSetOptions: [],
      }
    }

    if (userInterestedCurriculums.length === 1) {
      return {
        standardSetOptions: userInterestedCurriculums,
      }
    }

    if (userInterestedSubjects.length === 0) {
      return {
        standardSetOptions: userInterestedCurriculums,
      }
    }

    const _standardSetOptions = userInterestedCurriculums.filter((curriculum) =>
      userInterestedSubjects.includes(curriculum.subject)
    )

    if (_standardSetOptions.length === 1) {
      return {
        standardSetOptions: _standardSetOptions,
      }
    }

    if (_standardSetOptions.length > 1) {
      return {
        standardSetOptions: _standardSetOptions,
      }
    }

    return {
      standardSetOptions: userInterestedCurriculums,
    }
  } catch (error) {
    captureSentryException(error, {
      errorMessage:
        'AI Question Assistance - METADATA: Error getting standard set options for user curriculum input',
      interestedCurriculums,
      interestedSubjects,
    })

    return {
      standardSetOptions: [],
    }
  }
}

/**
 * Builds updated dictionaries alignments for UI sync.
 * @param {Array} standards - Resolved standards array (from resolveEffectiveMetadata)
 * @param {Array} allCurriculums - List of all curriculums from state
 * @param {Array} existingAlignments - Current alignments from dictionaries state
 * @returns {Array} - Updated alignments array
 */
export const buildDictAlignmentFromStandards = (
  standards,
  allCurriculums,
  existingAlignments = []
) => {
  if (!standards?.length) return existingAlignments

  const newStandards = standards.map((s) => pick(s, standardFields))
  const curriculumId = standards[0]?.curriculumId
  const curriculum = allCurriculums.find((c) => c._id === curriculumId)

  if (!curriculum) {
    captureSentryException(
      new Error('Curriculum not found while building alignment'),
      {
        errorMessage:
          'AI Question Assistance - METADATA: Curriculum not found while building alignment',
        standards: newStandards,
        curriculumId,
      }
    )
    return existingAlignments
  }

  const existingAlignmentIndex = (existingAlignments || []).findIndex(
    (a) => a.curriculumId === curriculumId
  )

  if (existingAlignmentIndex !== -1) {
    const existingStandards =
      existingAlignments?.[existingAlignmentIndex]?.standards || []
    const mergedStandards = [...existingStandards]
    for (const newStd of newStandards) {
      if (!existingStandards.some((s) => s._id === newStd._id)) {
        mergedStandards.push(newStd)
      }
    }

    return existingAlignments.map((alignment, index) =>
      index === existingAlignmentIndex
        ? { ...alignment, standards: mergedStandards }
        : alignment
    )
  }

  return [
    ...existingAlignments,
    {
      curriculum: curriculum.curriculum,
      curriculumId,
      subject: curriculum.subject,
      grades: [],
      standards: newStandards,
    },
  ]
}

/**
 * @param {Object} standard - Standard with `_id`
 * @param {Array<{ standard_id: string|number, confidence_score: number }>|undefined} standardIdsWithConfidence - from API `standard_ids`
 * @returns {number|undefined}
 */
export const getConfidenceScoreForStandard = (
  standard,
  standardIdsWithConfidence
) => {
  try {
    if (!standard?._id || !Array.isArray(standardIdsWithConfidence)) {
      throw new Error('Invalid arguments for confidence score')
    }

    const match = standardIdsWithConfidence.find(
      (item) => `${item?.standard_id}` === `${standard._id}`
    )

    if (!match) {
      return undefined
    }
    if (typeof match.confidence_score === 'number') {
      return match.confidence_score
    }
    throw new Error('Invalid confidence score for standard')
  } catch (error) {
    captureSentryException(error, {
      errorMessage:
        'AI Question Assistance - METADATA: Error getting confidence score for standard',
      standard,
      standardIdsWithConfidence,
    })
    return undefined
  }
}

export const isStandardAddedToQuestion = (question = {}) => {
  const alignments = question?.alignment || []
  return alignments.some(
    (alignment) =>
      alignment?.domains?.some((domain) => domain?.standards?.length > 0) ||
      alignment?.standards?.length > 0
  )
}
